import React, { useState, useEffect, useRef } from 'react';
import {
  Layout,
  List,
  Avatar,
  Input,
  Button,
  Typography,
  Space,
  Empty,
  Row,
  Col,
  Badge,
  Grid,
  message,
  Modal,
  Image,
} from 'antd';
import { SendOutlined, UserOutlined, SearchOutlined, ArrowLeftOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useAuth } from '@/contexts/AuthContext';
import { 
  sendMessageToThread, 
  markThreadMessagesAsRead,
  createChatThread,
  getUserChatThreadsOnce,
  getThreadMessagesOnce
} from '@/api/chat';
import { ChatThreadSummary, ChatMessage, ChatAttachment } from '@/types/chat';
import { useFirebaseMessaging } from '@/hooks/useFirebaseMessaging';
import { StartConversationModal } from '@/components/features/messaging/StartConversationModal';
import { Resident } from '@/types/resources/resident';
import { Employee } from '@/types/resources/employee';
import { Vendor } from '@/types/resources/vendor';
import { uploadFile } from '@/utils/fileUpload';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

// Ensure light theme for this component
const lightThemeStyles = `
  .messages-container .ant-layout-sider {
    background-color: #ffffff !important;
    color: #000000 !important;
  }
  .messages-container .ant-list-item {
    background-color: #ffffff !important;
    color: #000000 !important;
  }
  .messages-container .ant-list-item:hover {
    background-color: #f5f5f5 !important;
  }
  .messages-container .ant-typography {
    color: #000000 !important;
  }
  .messages-container .ant-input {
    background-color: #ffffff !important;
    color: #000000 !important;
  }
  .messages-container .ant-empty-description {
    color: #666666 !important;
  }
`;

export default function CommunityMessages() {
  const { t } = useTranslation('messages');
  const { firestoreUser } = useAuth();
  const { setupMessageListener } = useFirebaseMessaging();
  const [messageApi, contextHolder] = message.useMessage();
  
  // Updated state for new architecture
  const [chatThreads, setChatThreads] = useState<ChatThreadSummary[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedThreadMessages, setSelectedThreadMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showStartConversationModal, setShowStartConversationModal] = useState(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const screens = Grid.useBreakpoint();

  // Image viewer modal state
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ChatAttachment | null>(null);

  // Pagination state
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [hasRequestedOlderMessages, setHasRequestedOlderMessages] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef(0);

  // Message cache to avoid unnecessary fetches
  const [messageCache, setMessageCache] = useState<Record<string, ChatMessage[]>>({});
  
  // Prevent multiple executions
  const loadingRef = useRef(false);
  const loadedRef = useRef(false);

  // Load chat threads ONCE on component mount
  useEffect(() => {
    if (!firestoreUser?.workspace?.id || loadingRef.current || loadedRef.current) return;

    const loadThreads = async () => {
      if (loadingRef.current) return; // Prevent concurrent calls
      
      try {
        loadingRef.current = true;
        setIsLoading(true);
        
        // Use one-time fetch instead of real-time listener
        const threads = await getUserChatThreadsOnce(firestoreUser.id, firestoreUser.workspace!.id, firestoreUser);
        
        setChatThreads(threads);
        
        // Select first thread if none selected
        if (!selectedThreadId && threads.length > 0) {
          setSelectedThreadId(threads[0].id);
        }
        
        loadedRef.current = true;
      } catch (error) {
        console.error('❌ Failed to load chat threads:', error);
        messageApi.error('Failed to load conversations');
      } finally {
        setIsLoading(false);
        loadingRef.current = false;
      }
    };

    loadThreads();
  }, [firestoreUser?.id, firestoreUser?.workspace?.id]);

  // Load chat messages ON-DEMAND when thread is selected
  useEffect(() => {
    if (!selectedThreadId || !firestoreUser?.workspace?.id) return;



    const loadMessages = async () => {
      if (isLoadingMessages) return; // Block if already loading
      
      try {
        setIsLoadingMessages(true);
        // Check cache first
        if (messageCache[selectedThreadId]) {
          
          // Cached messages are already in correct order
          setSelectedThreadMessages(messageCache[selectedThreadId]);
          
          // Set pagination state based on cached message count
          setHasMoreMessages(messageCache[selectedThreadId].length >= 20);
          return;
        }

        // Fetch messages from Firestore
        const messages = await getThreadMessagesOnce(selectedThreadId, 20, firestoreUser); // Changed to 20 messages
        setSelectedThreadMessages(messages);
        
        // Cache the messages
        setMessageCache(prev => ({ ...prev, [selectedThreadId]: messages }));
        
        // Set pagination state - assume there are more messages if we got 20
        // We'll only set this to false when we actually get less than 20 messages
        const initialHasMoreMessages = messages.length >= 20;
        setHasMoreMessages(initialHasMoreMessages);
        

        
        // Mark messages as read
        await markThreadMessagesAsRead(selectedThreadId, firestoreUser.id, firestoreUser);
      } catch (error) {
        console.error('❌ Failed to load messages:', error);
        messageApi.error('Failed to load messages');
      } finally {
        setIsLoadingMessages(false);
      }
    };

    loadMessages();
  }, [selectedThreadId, firestoreUser?.id, firestoreUser?.workspace?.id]);

  // Setup FCM message listener ONLY - no Firestore listeners
  useEffect(() => {
    if (firestoreUser) {
      const unsubscribe = setupMessageListener((remoteMessage) => {
        // Handle new message from FCM
        if (remoteMessage.data?.type === 'chat_message') {
          
          const newMessage: ChatMessage = {
            id: remoteMessage.data!.messageId,
            fromUserId: remoteMessage.data!.senderId,
            message: remoteMessage.data!.messageText,
            timestamp: {
              toMillis: () => parseInt(remoteMessage.data!.timestamp) || Date.now(),
              toDate: () => new Date(parseInt(remoteMessage.data!.timestamp) || Date.now()),
              seconds: Math.floor((parseInt(remoteMessage.data!.timestamp) || Date.now()) / 1000),
              nanoseconds: 0,
              isEqual: () => false
            } as any,
            isRead: false
          };

          // Update current chat if it's the same thread
          if (remoteMessage.data!.threadId === selectedThreadId) {
            setSelectedThreadMessages(prev => {
              const updatedMessages = [...prev, newMessage];
              return updatedMessages;
            });
          }
          
          // Always update cache for the thread that received the message
          setMessageCache(prev => {
            const currentCache = prev[remoteMessage.data!.threadId] || [];
            const updatedCache = [...currentCache, newMessage];
            
            return {
              ...prev,
              [remoteMessage.data!.threadId]: updatedCache
            };
          });

          // Update threads list with new message
          setChatThreads(prev => prev.map(thread => {
            if (thread.id === remoteMessage.data!.threadId) {
              return {
                ...thread,
                lastMessage: {
                  id: remoteMessage.data!.messageId,
                  message: remoteMessage.data!.messageText,
                  fromUserId: remoteMessage.data!.senderId,
                  timestamp: newMessage.timestamp
                },
                unreadCount: thread.unreadCount + 1
              };
            }
            return thread;
          }));
        }
      });
      return unsubscribe;
    }
  }, [firestoreUser, selectedThreadId]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll to bottom when thread is first selected
  const scrollToBottomOnThreadSelect = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  };

  // Track if we're loading older messages to prevent auto-scroll
  const isInitialLoad = useRef(true);
  const previousMessageCount = useRef(0);

  useEffect(() => {
    // Only auto-scroll if this is a new message (not older messages being loaded)
    if (selectedThreadMessages.length > 0) {
      const currentCount = selectedThreadMessages.length;
      const previousCount = previousMessageCount.current;
      
      // If this is the first load or if we're adding messages at the end (new messages)
      if (isInitialLoad.current || currentCount > previousCount) {
        // Check if the new messages are at the end (new messages) or beginning (older messages)
        const lastMessageId = selectedThreadMessages[selectedThreadMessages.length - 1]?.id;
        const previousLastMessageId = selectedThreadMessages[previousCount - 1]?.id;
        
        // Only auto-scroll if the last message is new (not older messages being prepended)
        if (isInitialLoad.current || lastMessageId !== previousLastMessageId) {
          scrollToBottom();
        }
      }
      
      previousMessageCount.current = currentCount;
      isInitialLoad.current = false;
    }
  }, [selectedThreadMessages]);

  // Auto-scroll to bottom when thread is first selected (only on initial selection)
  useEffect(() => {
    if (selectedThreadId && selectedThreadMessages.length > 0 && isInitialLoad.current) {
      setTimeout(() => {
        scrollToBottomOnThreadSelect();
      }, 300);
    }
  }, [selectedThreadId]); // Removed selectedThreadMessages.length dependency

  // Reset initial load flag when switching threads
  useEffect(() => {
    if (selectedThreadId) {
      isInitialLoad.current = true;
      previousMessageCount.current = 0;
    }
  }, [selectedThreadId]);



  const loadOlderMessages = async () => {
    if (!selectedThreadId || !firestoreUser?.workspace?.id || isLoadingOlderMessages || !hasMoreMessages || hasRequestedOlderMessages || isLoadingMessages) {
      return;
    }

    try {
      setIsLoadingOlderMessages(true);
      setHasRequestedOlderMessages(true);
      
      // Store current scroll position before loading older messages
      const currentScrollTop = scrollContainerRef.current?.scrollTop || 0;
      
      // Get the oldest message to fetch messages before it
      const oldestMessage = selectedThreadMessages[0];
      if (!oldestMessage?.timestamp) {
        console.log('❌ No oldest message timestamp found');
        return;
      }
      
      // For proper pagination, we need to get the actual document snapshot
      // Since we don't have the document snapshot, we'll use a different approach
      // We'll fetch messages with a timestamp filter instead
      const oldestTimestamp = oldestMessage.timestamp;
      
      // Fetch older messages - ensure we only get exactly 20
      const olderMessages = await getThreadMessagesOnce(selectedThreadId, 20, firestoreUser, oldestTimestamp);
      
      if (olderMessages.length > 0) {
        // Ensure we only process exactly 20 messages (or less if that's all that's available)
        const messagesToAdd = olderMessages.slice(0, 20);
        
        // Older messages are now in correct order from API (oldest first)
        // Add older messages to the beginning of the array, but filter out duplicates
        const existingIds = new Set(selectedThreadMessages.map(m => m.id));
        const uniqueOlderMessages = messagesToAdd.filter(msg => !existingIds.has(msg.id));
        
        const updatedMessages = [...uniqueOlderMessages, ...selectedThreadMessages];
        
        setSelectedThreadMessages(updatedMessages);
        
        // Update cache
        setMessageCache(prev => ({
          ...prev,
          [selectedThreadId]: updatedMessages
        }));
        
        // Update pagination state - show more if we got any messages (meaning there are more to load)
        const newHasMoreMessages = uniqueOlderMessages.length > 0;
        setHasMoreMessages(newHasMoreMessages);
        setHasRequestedOlderMessages(false);
        

        
        // Maintain scroll position after loading older messages
        // Only do this if we actually added new messages
        if (uniqueOlderMessages.length > 0) {
          setTimeout(() => {
            if (scrollContainerRef.current) {
              // Calculate the actual height of the newly added older messages
              // by finding the first new message element and measuring its position
              // const messageElements = scrollContainerRef.current.querySelectorAll('[data-message-id]');
              let totalAddedHeight = 0;
              
              // Find the height from the first new message to the current scroll position
              for (let i = 0; i < uniqueOlderMessages.length; i++) {
                const messageId = uniqueOlderMessages[i].id;
                const messageElement = scrollContainerRef.current.querySelector(`[data-message-id="${messageId}"]`);
                if (messageElement) {
                  totalAddedHeight += (messageElement as HTMLElement).offsetHeight;
                }
              }
              
              // Fallback to estimated height if we can't measure actual elements
              if (totalAddedHeight === 0) {
                totalAddedHeight = uniqueOlderMessages.length * 80; // Approximate height per message
              }
              
              const newScrollTop = currentScrollTop + totalAddedHeight;
              scrollContainerRef.current.scrollTop = newScrollTop;

            }
          }, 100);
        }
      } else {
        // Only set hasMoreMessages to false if we actually got 0 messages
        // This means we've reached the end of the conversation
        setHasMoreMessages(false);
        setHasRequestedOlderMessages(false);

      }
    } catch (error) {
      console.error('❌ Failed to load older messages:', error);
      setHasRequestedOlderMessages(false);
    } finally {
      setIsLoadingOlderMessages(false);
    }
  };

  const handleSelectThread = async (threadId: string) => {
    setSelectedThreadId(threadId);
    
    // Reset pagination state for new thread
    setHasRequestedOlderMessages(false);
    setIsLoadingOlderMessages(false);
    
    // Load messages for this thread if not in cache
    if (!messageCache[threadId]) {
      try {
        const messages = await getThreadMessagesOnce(threadId, 20, firestoreUser); // Changed to 20 messages
        setSelectedThreadMessages(messages);
        
        // Cache the messages
        setMessageCache(prev => ({ ...prev, [threadId]: messages }));
        
        // Set pagination state based on initial message count
        setHasMoreMessages(messages.length >= 20);
      } catch (error) {
        console.error('❌ Failed to load messages for new thread:', error);
      }
    } else {
      // Cached messages are already in correct order
      // Set the cached messages to state immediately
      setSelectedThreadMessages(messageCache[threadId]);
      
      // Set pagination state based on cached message count
      setHasMoreMessages(messageCache[threadId].length >= 20);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThreadId || !firestoreUser?.workspace?.id) return;

    const messageText = newMessage.trim();
    setIsSending(true);

    try {
      // Create temporary message for immediate UI feedback
      const tempMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        fromUserId: firestoreUser.id,
        message: messageText,
        timestamp: {
          toMillis: () => Date.now(),
          toDate: () => new Date(),
          seconds: Math.floor(Date.now() / 1000),
          nanoseconds: 0,
          isEqual: () => false
        } as any, // Cast as any to satisfy the interface temporarily
        isRead: false
      };
      
      // Add to local chat history
      setSelectedThreadMessages(prev => [...prev, tempMessage]);
      
      // Clear input and scroll
      setNewMessage('');
      scrollToBottom();
      
      try {
        let actualThreadId = selectedThreadId;
        
        // If this is a temporary thread ID, create the actual thread
        if (selectedThreadId.startsWith('temp-thread-')) {
          console.log('Creating new thread for first message');
          
          try {
            // Get the other participant info from the selected thread
            const otherParticipant = selectedThreadInfo?.participantDetails.find(p => p.userId !== firestoreUser?.id);
            if (!otherParticipant) {
              throw new Error('Could not find other participant');
            }
            
            // Get the original participant info that was stored when creating the temporary thread
            const originalInfo = (selectedThreadInfo as any).originalParticipantInfo;
            if (!originalInfo) {
              throw new Error('Could not find original participant information');
            }
            
            // Create the thread with the first message
            actualThreadId = await createChatThread({
              participants: [otherParticipant.userId],
              workspaceId: firestoreUser.workspace.id,
              initialMessage: messageText,
              isGroupChat: false
            }, firestoreUser, firestoreUser, selectedThreadInfo?.participantDetails);
            
            // Update the selected thread ID
            setSelectedThreadId(actualThreadId);
            
            // Update the local threads state to replace the temporary thread
            setChatThreads(prev => prev.map(thread => 
              thread.id === selectedThreadId 
                ? { ...thread, id: actualThreadId }
                : thread
            ));
            
            console.log('New thread created with ID:', actualThreadId);
            
            // Clear the input and reset sending state since the message was already sent
            setNewMessage('');
            setIsSending(false);
            
            return; // Exit early to prevent duplicate message sending
          } catch (threadError: any) {
            console.error('Failed to create thread:', threadError);
            
            // If it's a permissions error, suggest using demo mode
            if (threadError.message?.includes('permissions') || threadError.message?.includes('Insufficient permissions')) {
              messageApi.error('Cannot create chat thread due to permissions. Please check your workspace access or use demo mode.');
            } else {
              messageApi.error('Failed to create chat thread. Please try again.');
            }
            
            // Remove the temporary message on error
            setSelectedThreadMessages(prev => prev.filter(msg => msg.id !== `temp-${Date.now()}`));
            setIsSending(false);
            return;
          }
        }
        
        // Send to Firestore
        const messageId = await sendMessageToThread(
          actualThreadId,
          messageText,
          firestoreUser.id,
          firestoreUser,
          attachments.length > 0 ? attachments : undefined
        );
        
        // Update the temporary message with the real ID
        setSelectedThreadMessages(prev => 
          prev.map(msg => 
            msg.id === `temp-${Date.now()}` 
              ? { ...msg, id: messageId }
              : msg
          )
        );
        
        // Clear attachments after successful send
        clearAttachments();
        
      } catch (error) {
        console.error('Failed to send message:', error);
        messageApi.error('Failed to send message. Please try again.');
        
        // Remove the temporary message on error
        setSelectedThreadMessages(prev => prev.filter(msg => msg.id !== `temp-${Date.now()}`));
      } finally {
        setIsSending(false);
      }
      
    } catch (error) {
      console.error('Failed to send message:', error);
      messageApi.error('Failed to send message. Please try again.');
      setIsSending(false);
    }
  };

  const handleStartConversationWithResident = async (resident: Resident) => {
    if (!firestoreUser?.workspace?.id) return;
    
    try {
      // Create a temporary thread ID - the actual thread will be created when first message is sent
      const tempThreadId = `temp-thread-${Date.now()}-${firestoreUser.id}`;
      
      // Create a temporary thread summary for the UI
      const tempThread: ChatThreadSummary = {
        id: tempThreadId,
        participantDetails: [
          {
            userId: firestoreUser.id,
            username: firestoreUser.displayName || firestoreUser.email || 'Current User',
            profilePicture: firestoreUser.profilePicture?.url || undefined
          },
          {
            userId: resident.user?.id || resident.id, // Use user.id if available, fallback to resident.id
            username: resident.user?.displayName || resident.contacts?.[0]?.name || 'Resident',
            profilePicture: undefined
          }
        ],
        lastMessage: {
          id: '',
          message: 'No messages yet',
          fromUserId: firestoreUser.id,
          timestamp: {
            toMillis: () => Date.now(),
            toDate: () => new Date(),
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: 0,
            isEqual: () => false
          } as any // Cast as any to satisfy the interface temporarily
        },
        unreadCount: 0,
        isGroupChat: false
      };
      
      // Store the participant information for later use when creating the real thread
      (tempThread as any).originalParticipantInfo = {
        residentId: resident.id,
        residentName: resident.user?.displayName || resident.contacts?.[0]?.name || 'Resident',
        residentProfilePicture: undefined
      };
      
      // Add to threads list
      setChatThreads(prev => [tempThread, ...prev]);
      
      // Select the thread to start chatting
      setSelectedThreadId(tempThreadId);
      
      // Close the modal
      setShowStartConversationModal(false);
      
      const residentName = resident.user?.displayName || resident.contacts?.[0]?.name || 'Resident';
      messageApi.success(`Started conversation with ${residentName}`);
      
    } catch (error) {
      console.error('Failed to start conversation:', error);
      messageApi.error('Failed to start conversation. Please try again.');
    }
  };

  const handleStartConversationWithEmployee = async (employee: Employee) => {
    if (!firestoreUser?.workspace?.id) return;
    
    try {
      // Create a temporary thread ID - the actual thread will be created when first message is sent
      const tempThreadId = `temp-thread-${Date.now()}-${firestoreUser.id}`;
      
      // Create a temporary thread summary for the UI
      const tempThread: ChatThreadSummary = {
        id: tempThreadId,
        participantDetails: [
          {
            userId: firestoreUser.id,
            username: firestoreUser.displayName || firestoreUser.email || 'Current User',
            profilePicture: firestoreUser.profilePicture?.url || undefined
          },
          {
            userId: employee.user?.id || employee.id, // Use user.id if available, fallback to employee.id
            username: employee.user?.displayName || employee.contacts?.[0]?.name || 'Employee',
            profilePicture: employee.user?.profilePicture?.url || undefined
          }
        ],
        lastMessage: {
          id: '',
          message: 'No messages yet',
          fromUserId: firestoreUser.id,
          timestamp: {
            toMillis: () => Date.now(),
            toDate: () => new Date(),
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: 0,
            isEqual: () => false
          } as any // Cast as any to satisfy the interface temporarily
        },
        unreadCount: 0,
        isGroupChat: false
      };
      
      // Store the participant information for later use when creating the real thread
      (tempThread as any).originalParticipantInfo = {
        employeeId: employee.id,
        employeeName: employee.user?.displayName || employee.contacts?.[0]?.name || 'Employee',
        employeeProfilePicture: employee.user?.profilePicture?.url
      };
      
      // Add to threads list
      setChatThreads(prev => [tempThread, ...prev]);
      
      // Select the thread to start chatting
      setSelectedThreadId(tempThreadId);
      
      // Close the modal
      setShowStartConversationModal(false);
      
      const employeeName = employee.user?.displayName || employee.contacts?.[0]?.name || 'Employee';
      messageApi.success(`Started conversation with ${employeeName}`);
      
    } catch (error) {
      console.error('Failed to start conversation:', error);
      messageApi.error('Failed to start conversation. Please try again.');
    }
  };

  const handleStartConversationWithVendor = async (vendor: Vendor) => {
    if (!firestoreUser?.workspace?.id) return;
    
    try {
      // Create a temporary thread ID - the actual thread will be created when first message is sent
      const tempThreadId = `temp-thread-${Date.now()}-${firestoreUser.id}`;
      
      // Create a temporary thread summary for the UI
      const tempThread: ChatThreadSummary = {
        id: tempThreadId,
        participantDetails: [
          {
            userId: firestoreUser.id,
            username: firestoreUser.displayName || firestoreUser.email || 'Current User',
            profilePicture: firestoreUser.profilePicture?.url || undefined
          },
          {
            userId: vendor.user?.id || vendor.id, // Use user.id if available, fallback to vendor.id
            username: vendor.user?.displayName || vendor.name || 'Vendor',
            profilePicture: vendor.user?.profilePicture?.url || undefined
          }
        ],
        lastMessage: {
          id: '',
          message: 'No messages yet',
          fromUserId: firestoreUser.id,
          timestamp: {
            toMillis: () => Date.now(),
            toDate: () => new Date(),
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: 0,
            isEqual: () => false
          } as any // Cast as any to satisfy the interface temporarily
        },
        unreadCount: 0,
        isGroupChat: false
      };
      
      // Store the participant information for later use when creating the real thread
      (tempThread as any).originalParticipantInfo = {
        vendorId: vendor.id,
        vendorName: vendor.user?.displayName || vendor.name || 'Vendor',
        vendorProfilePicture: vendor.user?.profilePicture?.url
      };
      
      // Add to threads list
      setChatThreads(prev => [tempThread, ...prev]);
      
      // Select the thread to start chatting
      setSelectedThreadId(tempThreadId);
      
      // Close the modal
      setShowStartConversationModal(false);
      
      const vendorName = vendor.user?.displayName || vendor.name || 'Vendor';
      messageApi.success(`Started conversation with ${vendorName}`);
      
    } catch (error) {
      console.error('Failed to start conversation:', error);
      messageApi.error('Failed to start conversation. Please try again.');
    }
  };

  // Image viewer functions
  const openImageModal = (attachment: ChatAttachment) => {
    setSelectedImage(attachment);
    setImageModalVisible(true);
  };

  const closeImageModal = () => {
    setImageModalVisible(false);
    setSelectedImage(null);
  };

  // File handling functions
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const newAttachments: ChatAttachment[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!firestoreUser?.id || !selectedThreadId) continue;
        
        try {
          const attachment = await uploadFile(file, firestoreUser.id, selectedThreadId);
          newAttachments.push(attachment);
        } catch (error) {
          console.error('Failed to upload file:', file.name, error);
          messageApi.error(`Failed to upload ${file.name}`);
        }
      }
      
      if (newAttachments.length > 0) {
        setAttachments(prev => [...prev, ...newAttachments]);
        messageApi.success(`Uploaded ${newAttachments.length} file(s)`);
      }
    } catch (error) {
      console.error('Error handling files:', error);
      messageApi.error('Failed to process files');
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const removeAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const clearAttachments = () => {
    setAttachments([]);
  };

  const filteredThreads = chatThreads.filter(thread => {
    if (!searchTerm.trim()) return true; // Show all if no search term
    
    try {
      // For group chats, search in group name
      if (thread.isGroupChat && thread.groupName) {
        return thread.groupName.toLowerCase().includes(searchTerm.toLowerCase());
      }
      
      // For 1-on-1 chats, search in participant names
      if (thread.participantDetails && Array.isArray(thread.participantDetails)) {
        const otherParticipant = thread.participantDetails.find((p: any) => p && p.userId !== firestoreUser?.id);
        if (otherParticipant && otherParticipant.username) {
          return otherParticipant.username.toLowerCase().includes(searchTerm.toLowerCase());
        }
      }
      
      return false;
    } catch (error) {
      console.warn('Error filtering thread:', thread.id, error);
      return false;
    }
  });

  const currentChatMessages = selectedThreadId ? selectedThreadMessages : [];
  const selectedThreadInfo = chatThreads.find(thread => thread.id === selectedThreadId);
  


  // Debug logging for messages


  if (isLoading) {
    return (
      <Layout style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="ant-spin ant-spin-lg ant-spin-spinning">
            <span className="ant-spin-dot ant-spin-dot-spin">
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
            </span>
          </div>
          <div style={{ marginTop: 16 }}>Loading conversations...</div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <style>{lightThemeStyles}</style>
      {contextHolder}
      <div 
        className="messages-container"
        style={{ 
          backgroundColor: '#ffffff', 
          color: '#000000',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Layout style={{ flex: 1, backgroundColor: '#ffffff' }}>
          <Sider 
            width={300} 
            style={{ 
              borderRight: screens.xs ? 0 : '1px solid #f0f0f0', 
              display: screens.xs && selectedThreadId ? 'none' : 'flex',
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              color: '#000000'
            }}
          >
            <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', backgroundColor: '#ffffff' }}>
              <Title level={4} style={{ margin: 0, color: '#000000' }}>Messages</Title>
              <Input
                placeholder="Search conversations..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ marginTop: 8 }}
              />
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#ffffff' }}>
              {filteredThreads.length === 0 ? (
                <Empty 
                  description="No conversations yet" 
                  style={{ margin: '32px 16px' }}
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={filteredThreads}
                  style={{flex: 1, overflowY: 'auto', backgroundColor: '#ffffff'}}
                  renderItem={thread => {

                    
                    // Get participant info from participantDetails array
                    let otherParticipant = null;
                    let displayName = 'Unknown User';
                    let avatarUrl = null;
                    
                    if (thread.isGroupChat) {
                      displayName = thread.groupName || 'Group Chat';
                      avatarUrl = thread.groupAvatar;
                    } else {
                      // Find other participant from participantDetails array
                      if (thread.participantDetails && Array.isArray(thread.participantDetails)) {
                        otherParticipant = thread.participantDetails.find(p => p && p.userId !== firestoreUser?.id);
                        if (otherParticipant) {
                          displayName = otherParticipant.username || 'Unknown User';
                          avatarUrl = otherParticipant.profilePicture;
                        }
                      }
                    }
                    

                    
                    return (
                      <List.Item
                        onClick={() => handleSelectThread(thread.id)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          backgroundColor: selectedThreadId === thread.id ? '#e6f7ff' : '#ffffff',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                      >
                        <List.Item.Meta
                          avatar={<Avatar src={avatarUrl} icon={!avatarUrl && <UserOutlined />} />} 
                          title={<Text strong style={{ color: '#000000' }}>{displayName}</Text>}
                          description={
                            <Text type="secondary" ellipsis style={{ color: '#666666' }}>
                              {thread.lastMessage?.message || t('startConversation', 'Start a conversation')}
                            </Text>
                          }
                        />
                        <Space direction="vertical" align="end" size={0}>
                          {thread.lastMessage?.timestamp && (
                            <Text type="secondary" style={{fontSize: '0.8em', color: '#999999'}}>
                              {(() => {
                                try {
                                  // Handle both Firestore Timestamps and JavaScript Dates
                                  if (thread.lastMessage.timestamp.toMillis) {
                                    // Firestore Timestamp
                                    return dayjs(thread.lastMessage.timestamp.toMillis()).format('h:mm A');
                                  } else if (thread.lastMessage.timestamp instanceof Date) {
                                    // JavaScript Date
                                    return dayjs(thread.lastMessage.timestamp).format('h:mm A');
                                  } else {
                                    // Unknown timestamp format
                                    return 'Unknown time';
                                  }
                                } catch (error) {
                                  console.warn('Error formatting timestamp:', error);
                                  return 'Invalid time';
                                }
                              })()}
                            </Text>
                          )}
                          {thread.unreadCount > 0 ? (
                            <Badge count={thread.unreadCount} size="small" />
                          ) : (
                            <div style={{height: 14}}/>
                          )}
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              )}
            </div>
            
            <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', backgroundColor: '#ffffff' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => setShowStartConversationModal(true)}
                block
              >
                New Conversation
              </Button>
            </div>
          </Sider>
          
          <Layout style={{ padding: '0', display: screens.xs && !selectedThreadId ? 'none' : 'flex', flex: 1 }}>
            {selectedThreadId && selectedThreadInfo ? (
              <Content style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center' }}>
                  {screens.xs && (
                    <Button 
                      icon={<ArrowLeftOutlined />} 
                      onClick={() => setSelectedThreadId(null)} 
                      style={{ marginRight: 16 }}
                      type="text"
                    />
                  )}
                  
                  <Avatar 
                    src={
                      selectedThreadInfo.isGroupChat ? 
                        selectedThreadInfo.groupAvatar : 
                        selectedThreadInfo.participantDetails.find((p: any) => p.userId !== firestoreUser?.id)?.profilePicture
                    } 
                    icon={
                      !(selectedThreadInfo.isGroupChat ? 
                        selectedThreadInfo.groupAvatar : 
                        selectedThreadInfo.participantDetails.find((p: any) => p.userId !== firestoreUser?.id)?.profilePicture) && 
                        <UserOutlined />
                    } 
                    style={{ marginRight: 12 }}
                  />
                  
                  <div>
                    <Text strong style={{ fontSize: '16px' }}>
                      {selectedThreadInfo.isGroupChat ? 
                        (selectedThreadInfo.groupName || 'Group Chat') : 
                        selectedThreadInfo.participantDetails.find((p: any) => p.userId !== firestoreUser?.id)?.username || 'Unknown User'
                      }
                    </Text>
                    {selectedThreadInfo.isGroupChat && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {selectedThreadInfo.participantDetails.length} participants
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
                
                <div 
                  ref={scrollContainerRef}
                  style={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    padding: '16px',
                    minHeight: 0
                  }}
                  onScroll={(e) => {
                    const target = e.target as HTMLDivElement;
                    const scrollTop = target.scrollTop;
                    scrollPositionRef.current = scrollTop;
                    
                    // Load older messages when scrolling to top (debounced)
                    if (scrollTop <= 50 && hasMoreMessages && !isLoadingOlderMessages && !hasRequestedOlderMessages && !isLoadingMessages) {
                      // Debounce the scroll handler to prevent multiple calls
                      clearTimeout((window as any).scrollTimeout);
                      (window as any).scrollTimeout = setTimeout(() => {
                        if (scrollTop <= 50 && hasMoreMessages && !isLoadingOlderMessages && !hasRequestedOlderMessages && !isLoadingMessages) {
                          loadOlderMessages();
                        }
                      }, 100);
                    }
                  }}
                >
                  {currentChatMessages.length === 0 ? (
                    <Empty 
                      description="No messages yet" 
                      style={{ margin: '32px 0' }}
                    />
                  ) : (
                    <div>
                      {/* Show "Scroll up to load more" indicator */}
                      {hasMoreMessages && selectedThreadMessages.length >= 20 && (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '16px', 
                          color: '#666',
                          borderBottom: '1px solid #f0f0f0',
                          marginBottom: '16px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef'
                        }}>
                          {isLoadingOlderMessages ? (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              gap: '8px'
                            }}>
                              <div className="ant-spin ant-spin-sm ant-spin-spinning">
                                <span className="ant-spin-dot ant-spin-dot-spin">
                                  <i className="ant-spin-dot-item"></i>
                                  <i className="ant-spin-dot-item"></i>
                                  <i className="ant-spin-dot-item"></i>
                                  <i className="ant-spin-dot-item"></i>
                                </span>
                              </div>
                              Loading older messages...
                            </div>
                          ) : (
                            <div style={{ 
                              color: '#495057',
                              fontWeight: '500'
                            }}>
                              Scroll up to load more messages
                            </div>
                          )}
                        </div>
                      )}
                      
                      {(() => {
                        // Debug: Check for duplicate message IDs in the entire array
                        const messageIds = currentChatMessages.map(m => m.id);
                        const duplicateIds = messageIds.filter((id, index) => messageIds.indexOf(id) !== index);
                        if (duplicateIds.length > 0) {
                          console.warn('⚠️ Duplicate message IDs found:', duplicateIds);
                          console.warn('⚠️ Full message array:', currentChatMessages.map(m => ({ id: m.id, message: m.message?.substring(0, 20) })));
                        }
                        
                        return currentChatMessages.map((msg, index) => {
                          // Show separator every 20 messages (after initial 20, then every 20 more)
                          if (index > 0 && index % 20 === 0) {
                          const separatorIndex = Math.floor(index / 20);
                          return (
                            <div key={`separator-${separatorIndex}`}>
                              <div 
                                data-separator="true"
                                data-index={separatorIndex}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  margin: '20px 0',
                                  color: '#666',
                                  backgroundColor: '#f8f9fa',
                                  padding: '8px 16px',
                                  borderRadius: '8px',
                                  border: '1px solid #e9ecef'
                                }}>
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                                <span style={{ 
                                  margin: '0 16px', 
                                  fontSize: '12px', 
                                  fontWeight: '500',
                                  color: '#495057'
                                }}>Earlier messages</span>
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                              </div>
                            </div>
                          );
                        }
                        
                                                // Regular message rendering
                        return (
                          <div
                            key={`${msg.id}-${index}-${msg.timestamp?.toMillis?.() || msg.timestamp || Date.now()}`}
                            data-message-id={msg.id}
                            style={{
                              marginBottom: '16px',
                              display: 'flex',
                              justifyContent: msg.fromUserId === firestoreUser?.id ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <div
                              style={{
                                maxWidth: '70%',
                                padding: '12px 16px',
                                borderRadius: '18px',
                                backgroundColor: msg.fromUserId === firestoreUser?.id ? '#1890ff' : '#f0f0f0',
                                color: msg.fromUserId === firestoreUser?.id ? 'white' : 'black',
                              }}
                            >
                              <Text style={{ color: msg.fromUserId === firestoreUser?.id ? 'white' : 'black' }}>
                                {msg.message}
                              </Text>
                              
                              {/* Display attachments if any */}
                              {msg.attachments && msg.attachments.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                  {msg.attachments.map((attachment) => {
                                    if (attachment.type === 'image') {
                                      return (
                                        <div 
                                          key={attachment.id}
                                          style={{ 
                                            marginTop: '8px',
                                            cursor: 'pointer',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            maxWidth: '200px'
                                          }}
                                          onClick={() => openImageModal(attachment)}
                                        >
                                          <Image
                                            src={attachment.url}
                                            alt={attachment.fileName}
                                            style={{ 
                                              width: '100%', 
                                              height: 'auto',
                                              borderRadius: '8px'
                                            }}
                                            preview={false}
                                          />
                                          <div style={{ 
                                            fontSize: '10px', 
                                            color: msg.fromUserId === firestoreUser?.id ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                            marginTop: '4px',
                                            textAlign: 'center'
                                          }}>
                                            {attachment.fileName}
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    // For other attachment types, show a placeholder
                                    return (
                                      <div 
                                        key={attachment.id}
                                        style={{ 
                                          marginTop: '8px',
                                          padding: '8px 12px',
                                          backgroundColor: msg.fromUserId === firestoreUser?.id ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                                          borderRadius: '8px',
                                          border: '1px solid',
                                          borderColor: msg.fromUserId === firestoreUser?.id ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
                                          cursor: 'pointer'
                                        }}
                                        onClick={() => window.open(attachment.url, '_blank')}
                                      >
                                        <div style={{ 
                                          fontSize: '12px', 
                                          color: msg.fromUserId === firestoreUser?.id ? 'white' : 'black',
                                          fontWeight: '500'
                                        }}>
                                          📎 {attachment.fileName}
                                        </div>
                                        {attachment.fileSize && (
                                          <div style={{ 
                                            fontSize: '10px', 
                                            color: msg.fromUserId === firestoreUser?.id ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                            marginTop: '2px'
                                          }}>
                                            {(attachment.fileSize / 1024 / 1024).toFixed(1)} MB
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              <div style={{ 
                                marginTop: '4px', 
                                fontSize: '12px', 
                                opacity: 0.7,
                                textAlign: msg.fromUserId === firestoreUser?.id ? 'right' : 'left'
                              }}>
                                {msg.timestamp ? 
                                  (() => {
                                    try {
                                      // Handle both Firestore Timestamps and JavaScript Dates
                                      if (msg.timestamp.toMillis) {
                                        // Firestore Timestamp
                                        return dayjs(msg.timestamp.toMillis()).format('h:mm A');
                                      } else if (msg.timestamp instanceof Date) {
                                        // JavaScript Date
                                        return dayjs(msg.timestamp).format('h:mm A');
                                      } else {
                                        // Unknown timestamp format
                                        return 'Unknown time';
                                      }
                                    } catch (error) {
                                      console.warn('Error formatting message timestamp:', error);
                                      return 'Invalid time';
                                    }
                                  })()
                                  : 'Sending...'
                                }
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
                
                <div style={{ 
                  padding: '16px', 
                  borderTop: '1px solid #f0f0f0',
                  flexShrink: 0,
                  backgroundColor: '#ffffff'
                }}>
                  {/* Attachments Preview */}
                  {attachments.length > 0 && (
                    <div style={{ 
                      marginBottom: '12px', 
                      padding: '8px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: '8px' 
                      }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>
                          Attachments ({attachments.length})
                        </Text>
                        <Button 
                          size="small" 
                          type="text" 
                          onClick={clearAttachments}
                          style={{ padding: '4px 8px' }}
                        >
                          Clear All
                        </Button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {attachments.map((attachment) => (
                          <div 
                            key={attachment.id}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '4px 8px',
                              backgroundColor: 'white',
                              borderRadius: '4px',
                              border: '1px solid #dee2e6',
                              fontSize: '12px'
                            }}
                          >
                            <span style={{ marginRight: '4px' }}>
                              {attachment.type === 'image' ? '🖼️' : '📎'}
                            </span>
                            <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {attachment.fileName}
                            </span>
                            <Button 
                              size="small" 
                              type="text" 
                              onClick={() => removeAttachment(attachment.id)}
                              style={{ padding: '2px 4px', marginLeft: '4px' }}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Row gutter={8}>
                    <Col>
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.txt,.csv,.mp4,.avi,.mov,.mp3,.wav,.aac,.m4a"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        id="file-upload"
                        disabled={isUploading}
                      />
                      <label htmlFor="file-upload">
                        <Button
                          icon={<PlusOutlined />}
                          loading={isUploading}
                          disabled={isUploading}
                        >
                          📎
                        </Button>
                      </label>
                    </Col>
                    <Col flex="1">
                      <TextArea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={t('typeMessagePlaceholder')}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        onPressEnter={(e) => {
                          if (!e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={isSending}
                      />
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                        loading={isSending}
                        disabled={(!newMessage.trim() && attachments.length === 0)}
                      />
                    </Col>
                  </Row>
                </div>
              </Content>
            ) : (
              <Content style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                flexDirection: 'column',
                padding: '32px',
                flex: 1,
                overflow: 'hidden'
              }}>
                <Empty 
                  description="Select a conversation to start messaging" 
                  style={{ margin: '32px 0' }}
                />
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={() => setShowStartConversationModal(true)}
                  size="large"
                >
                  Start New Conversation
                </Button>
              </Content>
            )}
          </Layout>

          <StartConversationModal
            visible={showStartConversationModal}
            onCancel={() => setShowStartConversationModal(false)}
            onSelectResident={handleStartConversationWithResident}
            onSelectEmployee={handleStartConversationWithEmployee}
            onSelectVendor={handleStartConversationWithVendor}
          />

          {/* Image Viewer Modal */}
          <Modal
            open={imageModalVisible}
            onCancel={closeImageModal}
            footer={null}
            width="90vw"
            style={{ top: 20 }}
            closeIcon={<CloseOutlined style={{ color: 'white', fontSize: '24px' }} />}
            styles={{
              body: { padding: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)' },
              mask: { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
            }}
            destroyOnClose
          >
            {selectedImage && (
              <div style={{ position: 'relative', height: '80vh' }}>
                <div style={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  padding: '20px', 
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  zIndex: 1000
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <Typography.Text style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                      {selectedImage.fileName}
                    </Typography.Text>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  padding: '60px 20px 20px 20px'
                }}>
                  <Image
                    src={selectedImage.url}
                    alt={selectedImage.fileName}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      objectFit: 'contain' 
                    }}
                    preview={false}
                  />
                </div>
              </div>
            )}
          </Modal>
        </Layout>
      </div>
    </>
  );
} 